import { configureRedact } from '../../shared/redact';

/** What a host passes to `jaioraBugs.configure()`; every field is optional, defaults are neutral. */
export interface WidgetConfig {
  /** API paths whose bodies carry people's words: their names and texts are hidden in the snapshot and learned. */
  personalUrl?: RegExp | null;
  /** API paths under `personalUrl` whose `name` is a title, not a person. */
  notPeopleUrl?: RegExp | null;
  /** Page places with people's names and words: hidden in the snapshot, HTML and clicks, plated on screenshots. */
  piiSelectors?: string[];
  /** Left out of the screenshot (heavy hidden tabs and the like). */
  skipSelectors?: string[];
  /** Host menu button and menu through which the report or the watch are opened: never a scenario step. */
  ownSelectors?: string[];
  /** Extra secret value shapes, hidden whole. */
  secretPatterns?: RegExp[];
  /** Prefix of `data-bug-src` in records (facts: `web/src/`). */
  srcPrefix?: string;
  /** Host elements moved aside while the report panel is open: `margin-right` / `right` = panel width. */
  makeRoom?: { marginRight?: string[]; right?: string[] };
  /** Extra fields of `clientState.app` (facts: which interface is on). */
  appState?: () => Record<string, unknown>;
  /** The watch pill's "open" button; default — `location.hash = '#ux-sessions'`. */
  onOpenUxSessions?: () => void;
  /** Any API error of the widget (401 — the host session is gone). */
  onApiError?: (error: { status: number; message: string; code?: string }) => void;
  /**
   * Help button in the bottom right corner (bug report, "watch me", "ask a question"); absent or null — no button, the
   * host opens the report itself. Set it when the host user is signed in, null on sign-out.
   */
  launcher?: LauncherConfig | null;
  /** What the report panel offers; a field absent — `DIALOG_DEFAULTS` (the short panel facts chose). */
  dialog?: DialogConfig | null;
  /**
   * "Что нового": the host's release notes and its "Где это?" index, shown as a screen of their own
   * (`jaioraBugs.mountWhatsNew(el)` on a host page) and entered from the help button's menu. Absent — no item, no screen.
   */
  whatsNew?: WhatsNewConfig | null;
  /** The tracker settings screen (`mountSettings`); absent — no prompt editors (report 63 of CRM). */
  settings?: SettingsConfig | null;
  /**
   * Build version of the code running in this tab (the short sha the host bakes into its bundle; a tab opened before a
   * deploy keeps the old one). Goes into the report (`clientState.appVersion`) and with every question to the service
   * (`launcher.ask.service`), which answers from the snapshot of exactly this version; absent — a guess only.
   */
  appVersion?: string | null;
}

export interface SettingsConfig {
  /**
   * Prompt editors of the service (system part read-only, user part editable): LLM instructions are shown only to a host
   * that asks for them — facts edits the service's prompts. Default false — no prompts anywhere on the screen.
   */
  prompts?: boolean;
}

export interface WhatsNewConfig {
  /**
   * The service keeps the notes and «Где это?» (28.10): the screen reads `<base>/bugs/api/whats-new` through the host proxy
   * — what the host's deploy published there (`PUT /api/whats-new/notes|index`, host/README.md) and what the agent sees by
   * MCP. Takes the place of `path`.
   */
  service?: boolean;
  /**
   * Host endpoint under the host base path (`/api/whats-new`): GET → `{ notes: WhatsNewNote[], index?: WhereEntry[] }`
   * (widget/src/whatsNew/types.ts). The notes stay in the host repo; the widget only shows them. Ignored with `service`.
   */
  path?: string;
  /** Opens the host page that mounts the screen (`/bug-reports/whats-new`); absent — no menu item. */
  open?: (() => void) | null;
  /** In-app navigation for links of the screen (a note's place, a "Где это?" place); absent — a plain page load. */
  navigate?: ((href: string) => void) | null;
}

export interface DialogConfig {
  /** @deprecated Removed from the panel (Quint facts report_tools П344): ignored. */
  shotButton?: boolean;
  /** @deprecated Removed from the panel (Quint facts report_tools П344): ignored. */
  imageButton?: boolean;
  /**
   * The host lets the person turn the shield off (report 101, Quint shot_shield Щт1–Щт3; facts gives it to its admins): with
   * the shield of the tools row off a shot is taken without hiding the marked names and people's data. Without it — or with
   * the shield on — every shot hides them (Мс3). Default false.
   */
  maskSwitch?: boolean;
  /** A dropped image becomes a shot of the current scenario, like a pasted one, not a file. Default true. */
  dropImagesAsShots?: boolean;
  /** "Ждал" of every scenario must be filled before sending; the field carries a required mark. Default true. */
  requireExpected?: boolean;
  /** A pasted or dropped image puts "[снимок N]" into the field at the caret / drop point; marks follow removals. Default true. */
  inlineShotMarks?: boolean;
  /** «Переформулировать» under the fields of a scenario (Рф1–Рф6); false — no button. Default true. */
  rephrase?: boolean;
  /** @deprecated Removed from the panel (Quint facts report_tools П344): ignored. */
  agentChat?: boolean;
  /** @deprecated Removed from the panel (Quint facts report_tools П344): ignored. */
  voice?: boolean;
  /** "Файлы" section; false — a pasted or dropped file that is not an image is turned down with a caption. Default false. */
  files?: boolean;
  /** @deprecated Removed from the panel (Quint facts report_tools П344): ignored. */
  recordSwitch?: boolean;
  /** @deprecated Removed from the panel (Quint facts report_tools П344): ignored. */
  scenarioButton?: boolean;
  /** @deprecated Removed from the panel (Quint facts report_tools П344): ignored. */
  htmlSwitch?: boolean;
}

export interface LauncherConfig {
  /** Bug report and "watch me" items; they are greyed out while the service does not let the user in. Default true. */
  bugs?: boolean;
  /** "Ask a question" about places on the screen; absent — no such item. */
  ask?: AskConfig | null;
  /**
   * "Репорты и вопросы" item: the host opens its tracker page (reports and stored questions) — the one entry into the
   * tracker. Super sees it as is; anyone else gets "Мои вопросы" when `ask` is on (the same host page then shows only
   * their own questions: `mountSettings` falls back to them when the service refuses the settings). Absent — no item.
   */
  openTracker?: (() => void) | null;
  /**
   * "Скрыть имена на экране" item, the beacon by the help button and Alt+Shift+M: all content of the page is blurred
   * except headings, buttons, statuses and counters (`SCREEN_OPEN` in screenMask.ts; report 184) — for showing the
   * screen; shots hide the same and, always, the marked names (`piiSelectors`). The widget's own windows (report
   * fields, thumbnails, questions) stay open (report 109). `true` — off until chosen, the choice is kept across reloads. `'default-on'`
   * (report 106) — on at every page load; switching it off lives until a reload. Default false — no item.
   */
  screenMask?: boolean | 'default-on';
  /**
   * A node of the host's frame for the help button (decision 12 of 29.10, facts report 219): the element or a CSS selector
   * — facts: its node in `.jui-appbar-end` by the bell. While the node is on the page the button is a quiet button inside
   * it and nowhere else, and `<html>` has the class `help-in-bar`; no node (not drawn yet, gone with a page change) — the
   * button floats in the corner as before, without the class. Absent — the corner.
   */
  slot?: HTMLElement | string | null;
}

export interface AskConfig {
  /**
   * The service answers (28.10): the question goes to `<base>/bugs/api/questions/ask` with `appVersion`; the service
   * checks proofs against the snapshot of that version and stores the question itself. The item is greyed out while the
   * service does not let the user in, like the report. Takes the place of `path`.
   */
  service?: boolean;
  /**
   * The host answers itself (older hosts): endpoint under the host base path (`/api/code-questions`): POST {question,
   * targets, route} → answer; the widget then stores the answered question in the tracker. Ignored with `service`.
   */
  path?: string;
  /** Attribute the host build puts on elements with `file:line` (facts: `data-src`); default `data-src`. */
  sourceAttr?: string;
  /**
   * "Проще" / "Подробнее" under every answer: asks the same question again at that depth (`depth`: simple | detailed;
   * new questions go as simple), the new answer replaces the old one, in the history too. Default false — no buttons,
   * no field.
   */
  depth?: boolean;
  /**
   * "Продукт / Разработчик" toggle above the input (`audience`: product | developer), held for the panel's next
   * questions. Default false — no toggle, no field.
   */
  audience?: boolean;
  /** @deprecated Former name of `depth` + `audience` together; kept for hosts that still pass it. */
  modes?: boolean;
  /**
   * The chat of the panel goes with a question as `history: [{question, answer}]` — all of it, held only by a ceiling in
   * characters (newest stay); any number above 0 turns it on, the number does not cut (Quint ask_chat Чт1). «Новый чат» empties it. Default 0 — none.
   */
  history?: number;
  /** A place's tooltip (`title` on it or near above) goes as `hint` when it is not its text; default false. */
  hints?: boolean;
  /**
   * Why the host cannot answer now (no model key, …): the panel says it as soon as it opens and does not send — no
   * question goes where no answer can come. Absent or empty — answers are on.
   */
  unavailable?: string | null;
}

/** Built in on every host: explicit marks. Host classes come through `piiSelectors`. */
const PII_BUILTIN = ['[data-pii]'];

interface State {
  config: WidgetConfig;
  notPeople: RegExp | null;
  pii: string;
  skip: string;
  own: string;
}

const state: State = { config: {}, notPeople: null, pii: PII_BUILTIN.join(', '), skip: '', own: '' };

// `test()` of a global regex moves `lastIndex` and alternates true/false between calls.
const stateless = (re: RegExp | null | undefined): RegExp | null => (re ? new RegExp(re.source, re.flags.replace(/[gy]/g, '')) : null);

/** Selectors the browser rejects are dropped one by one, so one typo does not switch masking off. */
function validSelectors(list: readonly string[] | undefined): string[] {
  const out: string[] = [];
  for (const raw of list ?? []) {
    const selector = typeof raw === 'string' ? raw.trim() : '';
    if (!selector) continue;
    try {
      if (typeof document !== 'undefined') document.createDocumentFragment().querySelector(selector);
      out.push(selector);
    } catch {
      console.warn(`[jaiora-bugs] selector skipped: ${selector}`);
    }
  }
  return out;
}

const roomListeners = new Set<() => void>();
const configListeners = new Set<() => void>();

/** Any `configure()` call; the overlay re-reads `launcher`. */
export function onConfigChange(fn: () => void): () => void {
  configListeners.add(fn);
  return () => configListeners.delete(fn);
}

export function onMakeRoomChange(fn: () => void): () => void {
  roomListeners.add(fn);
  return () => roomListeners.delete(fn);
}

const isRegExp = (value: unknown): value is RegExp => value instanceof RegExp;

/** A host passing a string where a RegExp is due must not switch the recorder off: such a field is dropped. */
function sane(input: WidgetConfig): WidgetConfig {
  const next: WidgetConfig = { ...(input && typeof input === 'object' ? input : {}) };
  const drop = (key: keyof WidgetConfig) => {
    console.warn(`[jaiora-bugs] configure: ${key} ignored`);
    delete next[key];
  };
  for (const key of ['personalUrl', 'notPeopleUrl'] as const) {
    if (next[key] !== undefined && next[key] !== null && !isRegExp(next[key])) drop(key);
  }
  if (next.secretPatterns !== undefined && !(Array.isArray(next.secretPatterns) && next.secretPatterns.every(isRegExp))) drop('secretPatterns');
  return next;
}

/** Merges into the current configuration: fields not passed keep their value. */
export function setConfig(input: WidgetConfig): void {
  const next = sane(input);
  const config = { ...state.config, ...next };
  state.config = config;
  state.notPeople = stateless(config.notPeopleUrl);
  state.pii = [...PII_BUILTIN, ...validSelectors(config.piiSelectors)].join(', ');
  state.skip = validSelectors(config.skipSelectors).join(', ');
  state.own = validSelectors(config.ownSelectors).join(', ');
  configureRedact({
    ...(next.personalUrl !== undefined ? { personalUrl: next.personalUrl } : {}),
    ...(next.secretPatterns !== undefined ? { secretPatterns: next.secretPatterns } : {}),
  });
  if (next.makeRoom !== undefined) for (const fn of roomListeners) fn();
  for (const fn of configListeners) fn();
}

/** Neutral start: nothing is personal, no host secrets — whatever the shared module defaults to. */
export function resetConfig(): void {
  state.config = {};
  state.notPeople = null;
  state.pii = PII_BUILTIN.join(', ');
  state.skip = '';
  state.own = '';
  configureRedact({ personalUrl: null, secretPatterns: [] });
  for (const fn of roomListeners) fn();
  for (const fn of configListeners) fn();
}

export const config = (): WidgetConfig => state.config;

/** `appVersion` as sent: trimmed, at most 64 chars; empty — null. */
export function appVersion(): string | null {
  const value = state.config.appVersion;
  return typeof value === 'string' && value.trim() ? value.trim().slice(0, 64) : null;
}
export const piiSelector = (): string => state.pii;
export const skipSelector = (): string => state.skip;
export const ownSelector = (): string => state.own;
export const srcPrefix = (): string => state.config.srcPrefix ?? '';
export const notPeopleUrl = (): RegExp | null => state.notPeople;
/**
 * The report panel when the host does not say otherwise — the short one facts settled on (its reports 69, 72, 76, 101):
 * area shot only, "[снимок N]" in the text, "Ждал" required, no agent, voice, files, recording,
 * second scenario or HTML switch. A host wanting a part back sets that field to true.
 */
export const DIALOG_DEFAULTS: Required<DialogConfig> = {
  shotButton: false,
  imageButton: false,
  maskSwitch: false,
  dropImagesAsShots: true,
  requireExpected: true,
  inlineShotMarks: true,
  rephrase: true,
  agentChat: false,
  voice: false,
  files: false,
  recordSwitch: false,
  scenarioButton: false,
  htmlSwitch: false,
};
/**
 * What the widget offers in this project, as the tracker set it («Проекты» → «Возможности»; model facts widget_features,
 * П315): a key the tracker set wins over the host's flags; a key it left out is still the host's to decide.
 */
export type FeatureValue = boolean | 'default-on';
export type ProjectFeatures = Partial<Record<string, FeatureValue>>;
let features: ProjectFeatures = {};
export function setProjectFeatures(next: ProjectFeatures | null | undefined): void {
  features = next && typeof next === 'object' ? next : {};
  for (const fn of configListeners) fn();
}
const feature = (key: string): FeatureValue | undefined => features[key];

const DIALOG_FEATURES = ['files'] as const;
/** Gone from the panel (Quint facts report_tools П344): no host flag and no tracker set turns them on; HTML always goes. */
const REMOVED_TOOLS = { agentChat: false, voice: false, recordSwitch: false, scenarioButton: false, shotButton: false, imageButton: false, htmlSwitch: false } as const;

export const dialogConfig = (): Required<DialogConfig> => {
  const value = state.config.dialog;
  const own = value && typeof value === 'object' ? value : {};
  const set = Object.fromEntries(Object.entries(own).filter(([, v]) => typeof v === 'boolean'));
  const tracker = Object.fromEntries(DIALOG_FEATURES.filter((key) => typeof feature(key) === 'boolean').map((key) => [key, feature(key)]));
  return { ...DIALOG_DEFAULTS, ...set, ...tracker, ...REMOVED_TOOLS };
};
export const launcherConfig = (): LauncherConfig | null => {
  const value = state.config.launcher;
  if (!value || typeof value !== 'object') return null;
  const out: LauncherConfig = { ...value };
  if (typeof feature('bug') === 'boolean') out.bugs = feature('bug') as boolean;
  if (feature('ask') === false) out.ask = null;
  // The service answers questions itself: the tracker can turn «Задать вопрос» on without the host's own path.
  else if (feature('ask') === true && !out.ask) out.ask = { service: true };
  if (feature('mask') !== undefined) out.screenMask = feature('mask');
  if (feature('tracker') === false) out.openTracker = null;
  return out;
};

/**
 * What the site's own code turns on, before the tracker's word, and the hooks it gave (features_screen П329): the widget
 * reports it, so «Возможности» shows how the site works until the project's set is saved.
 */
export function hostOwnState(): { features: Record<string, FeatureValue>; hooks: { news: boolean; tracker: boolean } } {
  const launcher = state.config.launcher && typeof state.config.launcher === 'object' ? state.config.launcher : null;
  const own = state.config.dialog && typeof state.config.dialog === 'object' ? state.config.dialog : {};
  const dialog: Required<DialogConfig> = { ...DIALOG_DEFAULTS, ...Object.fromEntries(Object.entries(own).filter(([, v]) => typeof v === 'boolean')) };
  // Хс1: a screen is a source of notes (service or path) and the host page that shows it.
  const news = state.config.whatsNew;
  const hasNews = !!news && typeof news === 'object' && (news.service === true || (typeof news.path === 'string' && !!news.path)) && typeof news.open === 'function';
  const hasTracker = typeof launcher?.openTracker === 'function';
  const mask = launcher?.screenMask;
  return {
    features: {
      bug: !!launcher && launcher.bugs !== false,
      ux: !!launcher && launcher.bugs !== false,
      ask: !!launcher?.ask,
      news: hasNews,
      mask: mask === 'default-on' ? 'default-on' : mask === true,
      tracker: hasTracker,
      ...Object.fromEntries(DIALOG_FEATURES.map((key) => [key, dialog[key]])),
    },
    hooks: { news: hasNews, tracker: hasTracker },
  };
}

/** «Последи за мной» (П315, П316): the tracker's word, else as the report items. */
export const uxWatchOffered = (): boolean => (typeof feature('ux') === 'boolean' ? (feature('ux') as boolean) : launcherConfig()?.bugs !== false);
/** The report itself, by the button or by `jaioraBugs.open()` (П316). */
export const bugReportOffered = (): boolean => feature('bug') !== false;

/** The tracker screen as the host wants it; prompts only when the host said `prompts: true`. */
export const settingsConfig = (): Required<SettingsConfig> => {
  const value = state.config.settings;
  return { prompts: !!value && typeof value === 'object' && value.prompts === true };
};

/** П317, Хс1: the tracker turned «Что нового» on, the host has no screen for it (gone since) — the item stays grey with the reason. */
export const whatsNewWithoutPage = (): boolean => feature('news') === true && typeof whatsNewConfig()?.open !== 'function';

export const whatsNewConfig = (): WhatsNewConfig | null => {
  if (feature('news') === false) return null;
  const value = state.config.whatsNew;
  if (!value || typeof value !== 'object') return null;
  return value.service === true || (typeof value.path === 'string' && value.path) ? value : null;
};

/**
 * How far the page actually moved aside for the open panel (Хс3): the largest `margin-right` / `right` the `makeRoom`
 * elements got; no `makeRoom`, no such element or a narrow window — 0, the panel lies over the page.
 */
export function pageShift(): number {
  if (typeof document === 'undefined') return 0;
  const html = document.documentElement;
  if (!html.classList.contains('bug-panel-open') || window.innerWidth <= 600) return 0;
  const room = state.config.makeRoom;
  const panel = parseFloat(getComputedStyle(html).getPropertyValue('--bug-w')) || 0;
  const px = (value: string): number => {
    const n = parseFloat(value);
    if (Number.isFinite(n)) return n;
    // An engine that leaves var() unresolved (jsdom).
    return /var\(--bug-w\b/.test(value) ? panel : 0;
  };
  let shift = 0;
  const measure = (list: string[] | undefined, prop: 'marginRight' | 'right') => {
    for (const selector of validSelectors(list)) {
      for (const el of document.querySelectorAll(selector)) shift = Math.max(shift, px(getComputedStyle(el)[prop]));
    }
  };
  measure(room?.marginRight, 'marginRight');
  measure(room?.right, 'right');
  return Math.round(shift);
}

/** CSS that moves host elements aside for the panel: built from `makeRoom`, unscoped on purpose (it targets the host). */
export function makeRoomCss(): string {
  const room = state.config.makeRoom;
  const margin = validSelectors(room?.marginRight);
  const right = validSelectors(room?.right);
  if (margin.length === 0 && right.length === 0) return '';
  const under = (list: string[]) => list.map((selector) => `html.bug-panel-open ${selector}`).join(',\n');
  const rules: string[] = [];
  const narrow: string[] = [];
  if (margin.length) {
    rules.push(`${under(margin)} { margin-right: var(--bug-w, 420px); }`);
    narrow.push(`${under(margin)} { margin-right: 0; }`);
  }
  if (right.length) {
    rules.push(`${under(right)} { right: var(--bug-w, 420px); }`);
    narrow.push(`${under(right)} { right: 0; }`);
  }
  return `${rules.join('\n')}\n@media (max-width: 600px) {\n${narrow.join('\n')}\n}\n`;
}
